import { Button, Portal } from '@mantine/core';
import { IconVariable } from '@tabler/icons-react';
import type { Config } from 'jodit/esm/config';
import type { DeepPartial } from 'jodit/esm/types';
import type { IJodit } from 'jodit/esm/types/jodit';
import JoditEditor from 'jodit-react';
import { i18nKeys, useTranslation } from 'locales';
import { useCallback, useMemo, useRef, useState } from 'react';
import { isEmpty, isNullish } from 'remeda';
import { draftKey, useTemplateDraftsAtom } from './state';
import styles from './TemplateBodyEditor.module.scss';
import { getToolbarIcon } from './toolbar-icons';
import {
  type TemplateVariableItem,
  useGetVariableCategoryLabel,
  useGetVariableLabel,
  useTemplateVariableCategories,
} from './use-template-variables';
import { VARIABLE_SLASH_TRIGGER, VariableSuggestionPopup } from './VariableSuggestionList';
import {
  escapeHtml,
  pillsToVariables,
  renderVariablePill,
  variablesToPills,
} from './variable-serialization';

type TemplateBodyEditorProps = {
  templateId: string;
  channel: string;
  locale: string;
  content: string;
  planType: string;
};

type VariablePicker =
  | { mode: 'insert'; node: Node; start: number; end: number; rect: DOMRect }
  | { mode: 'edit'; element: HTMLElement; rect: DOMRect; variable: string }
  | { mode: 'toolbar'; range: Range; rect: DOMRect };

export const TemplateBodyEditor = ({
  templateId,
  channel,
  locale,
  content,
  planType,
}: TemplateBodyEditorProps) => {
  const { currentLang, t } = useTranslation();
  const [drafts, setDrafts] = useTemplateDraftsAtom();
  const categories = useTemplateVariableCategories(planType);

  const bodyKey = draftKey(templateId, channel, locale);
  const initialContent = useRef(drafts[bodyKey] ?? content).current;

  const [picker, setPicker] = useState<VariablePicker | null>(null);
  const [toolbarSlot, setToolbarSlot] = useState<HTMLElement | null>(null);
  const editorRef = useRef<IJodit | null>(null);

  const getVariableLabel = useGetVariableLabel(categories);
  const getVariableCategoryLabel = useGetVariableCategoryLabel(categories);

  const getVariableLabelRef = useRef(getVariableLabel);
  getVariableLabelRef.current = getVariableLabel;

  const editorConfig = useMemo(
    () => ({
      ...HTML_PRESERVING_CONFIG,
      language: currentLang,
      i18n: {
        [currentLang]: {
          'Paste as HTML': t(i18nKeys.TEMPLATE.PASTE_DIALOG.FORMATTED_TITLE),
          'Your code is similar to HTML. Keep as HTML?': t(
            i18nKeys.TEMPLATE.PASTE_DIALOG.FORMATTED_MESSAGE,
          ),
          'Word Paste Detected': t(i18nKeys.TEMPLATE.PASTE_DIALOG.WORD_TITLE),
          'The pasted content is coming from a Microsoft Word/Excel document. Do you want to keep the format or clean it up?':
            t(i18nKeys.TEMPLATE.PASTE_DIALOG.WORD_MESSAGE),
          Keep: t(i18nKeys.TEMPLATE.PASTE_DIALOG.KEEP_FORMATTING),
          Clean: t(i18nKeys.TEMPLATE.PASTE_DIALOG.CLEAN_UP_FORMATTING),
          'Insert only Text': t(i18nKeys.TEMPLATE.PASTE_DIALOG.PLAIN_TEXT),
        },
      },
    }),
    [currentLang, t],
  );

  const registerEditor = useCallback((editor: IJodit) => {
    editorRef.current = editor;

    editor.e
      .on('beforeSetValueToEditor.templateVariable', (value: unknown) =>
        typeof value === 'string'
          ? variablesToPills(value, getVariableLabelRef.current)
          : undefined,
      )
      .on('afterGetValueFromEditor.templateVariable', (payload: { value: string }) => {
        payload.value = pillsToVariables(payload.value);
      })
      .on('keyup.templateVariable', () => setPicker(findSlashTrigger(editor)));

    let isPlainTextPastePending = false;

    editor.e
      .on('keydown.plainTextPaste', (event: KeyboardEvent) => {
        isPlainTextPastePending = isPlainTextPasteKeystroke(event);
      })
      .on('keyup.plainTextPaste', () => {
        isPlainTextPastePending = false;
      });

    editor.waitForReady().then(() => {
      if (isNullish(editor.editor)) return;

      editor.e.on(
        editor.editor,
        'paste',
        (event: ClipboardEvent) => {
          if (!isPlainTextPastePending) return;
          isPlainTextPastePending = false;

          const text = event.clipboardData?.getData('text/plain') ?? '';
          if (isEmpty(text)) return;

          editor.s.insertHTML(plainTextToHtml(text));
          return false;
        },
        { capture: true },
      );

      const collection = editor.toolbar.container;
      setToolbarSlot(collection.querySelector(TOOLBAR_LINE_SELECTOR) ?? collection);

      editor.e.on(editor.editor, 'click', (event: MouseEvent) => {
        const pill = (event.target as Element | null)?.closest?.(VARIABLE_PILL_SELECTOR);

        setPicker(
          pill instanceof HTMLElement
            ? {
                mode: 'edit',
                element: pill,
                rect: pill.getBoundingClientRect(),
                variable: pill.dataset.variable ?? '',
              }
            : null,
        );
      });

      editor.setEditorValue(editor.getEditorValue());
    });
  }, []);

  const saveDraft = useCallback(
    (body: string) => setDrafts(previous => ({ ...previous, [bodyKey]: body })),
    [bodyKey, setDrafts],
  );

  const getPickerRange = (editor: IJodit): Range | null => {
    if (isNullish(picker)) return null;

    const range = editor.ed.createRange();

    if (picker.mode === 'edit') {
      if (!picker.element.isConnected) return null;
      range.selectNode(picker.element);
      return range;
    }

    if (picker.mode === 'toolbar') {
      return picker.range.startContainer.isConnected ? picker.range : null;
    }

    if (!picker.node.isConnected) return null;
    range.setStart(picker.node, picker.start);
    range.setEnd(picker.node, picker.end);
    return range;
  };

  const applyVariable = (item: TemplateVariableItem) => {
    const editor = editorRef.current;
    const range = isNullish(editor) ? null : getPickerRange(editor);

    if (isNullish(editor) || isNullish(range)) {
      setPicker(null);
      return;
    }

    const pill = renderVariablePill(item.value, getVariableLabel(item.value));
    range.deleteContents();
    editor.s.selectRange(range);
    editor.s.insertHTML(picker?.mode === 'edit' ? pill : `${pill}&nbsp;`);

    setPicker(null);
  };

  const openPickerFromToolbar = (button: HTMLButtonElement) => {
    const editor = editorRef.current;
    if (isNullish(editor)) return;

    setPicker({
      mode: 'toolbar',
      range: editor.s.range.cloneRange(),
      rect: button.getBoundingClientRect(),
    });
  };

  const dismissPicker = () => {
    const editor = editorRef.current;
    const range = picker?.mode === 'insert' && editor ? getPickerRange(editor) : null;

    if (editor && range) {
      range.deleteContents();
      editor.s.selectRange(range);
    }

    setPicker(null);
  };

  return (
    <div className={styles.editorContainer}>
      <JoditEditor
        value={initialContent}
        config={editorConfig}
        onChange={saveDraft}
        editorRef={registerEditor}
      />

      {toolbarSlot && (
        <Portal target={toolbarSlot}>
          <Button
            variant="default"
            size="xs"
            ml="auto"
            mr="xs"
            style={{ alignSelf: 'center' }}
            leftSection={<IconVariable size={16} stroke={1.5} />}
            onMouseDown={event => event.preventDefault()}
            onClick={event => openPickerFromToolbar(event.currentTarget)}
          >
            {t(i18nKeys.ADD_VARIABLE)}
          </Button>
        </Portal>
      )}

      {picker && (
        <VariableSuggestionPopup
          anchor={picker.rect}
          categories={categories}
          initialCategoryLabel={
            picker.mode === 'edit' ? getVariableCategoryLabel(picker.variable) : null
          }
          command={applyVariable}
          onClose={dismissPicker}
        />
      )}
    </div>
  );
};

const VARIABLE_PILL_SELECTOR = '[data-variable]';

const LINE_BREAK = /\r?\n/g;

const isPlainTextPasteKeystroke = (event: KeyboardEvent) =>
  event.shiftKey &&
  (event.metaKey || event.ctrlKey) &&
  (event.key.toLowerCase() === 'v' || event.code === 'KeyV');

const plainTextToHtml = (text: string) => escapeHtml(text).replace(LINE_BREAK, '<br>');

const findSlashTrigger = (editor: IJodit): VariablePicker | null => {
  const selection = editor.s.range;
  if (!selection.collapsed) return null;

  const node = selection.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return null;
  const textBeforeCursor = (node.textContent ?? '').slice(0, selection.startOffset);
  if (!VARIABLE_SLASH_TRIGGER.test(textBeforeCursor)) return null;

  const range = editor.ed.createRange();
  range.setStart(node, selection.startOffset - 1);
  range.setEnd(node, selection.startOffset);

  return {
    mode: 'insert',
    node,
    start: selection.startOffset - 1,
    end: selection.startOffset,
    rect: range.getBoundingClientRect(),
  };
};

// Jodit lays its buttons out inside a single full-width "line" group; appending
// there keeps the button on the toolbar row instead of wrapping onto a new one.
const TOOLBAR_LINE_SELECTOR = '.jodit-ui-group_line_true';

const EDITOR_BUTTONS = [
  'undo',
  'redo',
  '|',
  'bold',
  'italic',
  'underline',
  '|',
  'eraser',
  '|',
  'brush',
  'font',
  'fontsize',
  '|',
  'hr',
  'ul',
  'ol',
  '|',
  'link',
  'unlink',
  'image',
  'table',
  '|',
  'left',
  'center',
  'justify',
  'right',
  '|',
  'source',
];

// `getIcon` lives on Jodit's view options, which the package does not export,
// so it is declared alongside the config type here.
type EditorConfig = DeepPartial<Config> & {
  getIcon?: (name: string, clearName: string) => string | undefined;
};

const HTML_PRESERVING_CONFIG: EditorConfig = {
  height: 600,
  toolbarAdaptive: false,
  toolbarSticky: false,
  statusbar: false,
  allowResizeY: false,
  hidePoweredByJodit: true,
  buttons: EDITOR_BUTTONS,
  getIcon: getToolbarIcon,
  disablePlugins: ['pasteStorage'],
  askBeforePasteHTML: true,
  askBeforePasteFromWord: true,
  pasteHTMLActionList: [
    { value: 'insert_as_html', text: 'Keep' },
    { value: 'insert_only_text', text: 'Insert only Text' },
  ],
  defaultActionOnPaste: 'insert_as_html',
  cleanHTML: {
    replaceNBSP: false,
    removeEmptyElements: false,
    fillEmptyParagraph: false,
    replaceOldTags: false,
    safeLinksTarget: false,
  },
  dtd: {
    removeExtraBr: false,
    checkBlockNesting: false,
  },
};
